/**
 * CouponCodeWidgetPage — read-only report for `coupon-code-widget`.
 * Top coupons by redemptions/revenue/unique_users. GET /api/v1/coupon-code-widget.
 */
import { useState } from "react";
import { useCouponCodeWidgetList } from "./hooks";
import type { CouponSort } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const BTN_BASE =
	"rounded-[6px] border px-3 py-1.5 text-xs font-medium transition-colors";
const BTN_ACTIVE = "border-green-600 bg-green-50 text-green-700";
const BTN_IDLE = "border-zinc-200 bg-white text-zinc-600 hover:border-green-300";

function fmtInt(n: number): string {
	return n.toLocaleString();
}

function fmtDollars(cents: number): string {
	return `$${(cents / 100).toLocaleString(undefined, {
 minimumFractionDigits: 2,
 maximumFractionDigits: 2,
	})}`;
}

const SORT_OPTIONS: Array<{ key: CouponSort; label: string }> = [
	{ key: "redemptions", label: "Redemptions" },
	{ key: "revenue", label: "Revenue" },
	{ key: "unique_users", label: "Unique users" },
];

export function CouponCodeWidgetPage() {
	const [sort, setSort] = useState<CouponSort>("redemptions");
	const listQuery = useCouponCodeWidgetList({ sort });
	const items = listQuery.data?.items ?? [];

	return (
 <main
 data-testid="coupon-code-widget-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-green-700">
 Report
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Coupon code widget
 </h1>
 <p className="text-sm text-zinc-500">
 Top-performing coupon codes across redemption windows. Pick a sort to reorder.
 </p>
 </header>

 <section className={CARD} aria-label="Coupon code widget report">
 <div
 className="mb-4 flex flex-wrap items-center gap-2"
 data-testid="coupon-code-widget-sort"
 >
 <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 Sort
 </span>
 {SORT_OPTIONS.map((opt) => {
 const active = sort === opt.key;
 return (
 <button
 key={opt.key}
 type="button"
 onClick={() => setSort(opt.key)}
 aria-pressed={active}
 data-testid={`coupon-code-widget-sort-${opt.key}`}
 className={`${BTN_BASE} ${active ? BTN_ACTIVE : BTN_IDLE}`}
 >
 {opt.label}
 </button>
 );
 })}
 </div>

 {listQuery.isLoading ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="coupon-code-widget-loading"
 >
 Loading.
 </p>
 ) : listQuery.error ? (
 <p className="text-sm text-red-600" role="alert">
 {listQuery.error.message}
 </p>
 ) : items.length === 0 ? (
 <p className="text-sm text-zinc-500" data-testid="coupon-code-widget-empty">
 No coupon data yet.
 </p>
 ) : (
 <div className="overflow-x-auto">
 <table
 className="w-full border-collapse"
 data-testid="coupon-code-widget-table"
 >
 <thead className="bg-zinc-50">
 <tr>
 <th className={TH}>Coupon</th>
 <th className={TH}>Period</th>
 <th className={TH}>Redemptions</th>
 <th className={TH}>Revenue</th>
 <th className={TH}>Unique users</th>
 <th className={TH}>Avg order</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-zinc-200">
 {items.map((row) => (
 <tr
 key={row.id}
 data-testid={`coupon-code-widget-row-${row.id}`}
 >
 <td className={`${TD} font-medium text-green-700`}>
 {row.coupon_code}
 </td>
 <td className={`${TD} text-zinc-500`}>
 {row.period_start}
 <span className="px-1 text-zinc-300">→</span>
 {row.period_end}
 </td>
 <td className={`${TD} tabular-nums`}>
 {fmtInt(row.redemptions)}
 </td>
 <td className={`${TD} tabular-nums`}>
 {fmtDollars(row.revenue_cents)}
 </td>
 <td className={`${TD} tabular-nums`}>
 {fmtInt(row.unique_users)}
 </td>
 <td className={`${TD} tabular-nums text-zinc-500`}>
 {row.avg_order_cents === null
 ? "—"
 : fmtDollars(row.avg_order_cents)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>
 </main>
	);
}

export default CouponCodeWidgetPage;
